import { useEffect, useMemo, useRef, useState } from "react";
import type {
  AcceleratorCatalogEntry,
  AcceleratorInstallationDTO,
  AcceleratorInstallationStatus,
} from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";
import { useConnectionContext } from "@/lib/connectionContext";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { InstallDialerDialog } from "./InstallDialerDialog";

type CatalogState =
  | { status: "loading" }
  | { status: "ready"; entries: AcceleratorCatalogEntry[] }
  | { status: "error"; message: string };

const POLL_MS = 2500;

/**
 * Accelerators catalog — pre-built Twilio solutions a tenant can install into a
 * connected account. A Dialer can be deployed on any number of voice numbers,
 * across accounts; each deployment is listed under the card. Installs run
 * out-of-band; this page polls in-flight ones until they settle.
 */
export function AcceleratorsPage() {
  const ctx = useConnectionContext();
  const [catalog, setCatalog] = useState<CatalogState>({ status: "loading" });
  const [installs, setInstalls] = useState<AcceleratorInstallationDTO[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [removal, setRemoval] = useState<AcceleratorInstallationDTO | null>(null);
  const [removing, setRemoving] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;
    setCatalog({ status: "loading" });
    void Promise.all([api.accelerators.list(), api.accelerators.listInstallations()])
      .then(([cat, inst]) => {
        if (cancelled) return;
        setCatalog({ status: "ready", entries: [...cat.accelerators] });
        setInstalls(inst.installations);
      })
      .catch((err) => {
        if (cancelled) return;
        setCatalog({
          status: "error",
          message: err instanceof ApiClientError ? err.message : "Failed to load accelerators",
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Installations grouped by accelerator, then collapsed to one row per number
  // (a number binds to one TwiML App — a superseded failed attempt is hidden
  // once the number has a live dialer). Input is newest-first from the API.
  const installsByAccel = useMemo(() => {
    const map = new Map<string, AcceleratorInstallationDTO[]>();
    for (const inst of installs) {
      const list = map.get(inst.acceleratorId) ?? [];
      list.push(inst);
      map.set(inst.acceleratorId, list);
    }
    for (const [id, list] of map) map.set(id, dedupeByNumber(list));
    return map;
  }, [installs]);

  const connNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of ctx.connections) map.set(c.id, c.friendlyName ?? c.accountSid);
    return map;
  }, [ctx.connections]);

  // Poll any installation that's still settling.
  const pendingIds = useMemo(
    () =>
      installs
        .filter((i) => i.status === "pending" || i.status === "running")
        .map((i) => i.id)
        .sort()
        .join(","),
    [installs],
  );

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!pendingIds) return;
    const ids = pendingIds.split(",");
    pollRef.current = setInterval(() => {
      for (const id of ids) {
        void api.accelerators
          .getInstallation(id)
          .then((updated) => {
            setInstalls((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
          })
          .catch(() => {
            /* transient — next tick retries */
          });
      }
    }, POLL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [pendingIds]);

  const activeConnections = useMemo(
    () => ctx.connections.filter((c) => c.status === "active"),
    [ctx.connections],
  );

  function onInstalled(dtos: AcceleratorInstallationDTO[]) {
    setDialogOpen(false);
    if (dtos.length === 0) return;
    const ids = new Set(dtos.map((d) => d.id));
    setInstalls((prev) => [...dtos, ...prev.filter((i) => !ids.has(i.id))]);
  }

  async function confirmRemoval() {
    if (!removal) return;
    setRemoving(true);
    try {
      await api.accelerators.uninstall(removal.id);
      setInstalls((prev) => prev.filter((i) => i.id !== removal.id));
      setRemoval(null);
    } catch {
      /* keep the dialog open; the row stays so the user can retry */
    } finally {
      setRemoving(false);
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
          Deploy
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
          Accelerators
        </h1>
        <p className="mt-1 max-w-2xl text-[13.5px] text-zinc-600 dark:text-zinc-400">
          One-click solutions that deploy straight into your connected Twilio accounts. Pick one,
          choose your numbers, and they&apos;re live — no console wiring.
        </p>
      </header>

      {catalog.status === "loading" && <SkeletonGrid />}

      {catalog.status === "error" && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {catalog.message}
        </p>
      )}

      {catalog.status === "ready" && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {catalog.entries.map((entry) => (
            <AcceleratorCard
              key={entry.id}
              entry={entry}
              installations={installsByAccel.get(entry.id) ?? []}
              connNameById={connNameById}
              onInstall={() => setDialogOpen(true)}
              onRemove={(inst) => setRemoval(inst)}
            />
          ))}
        </div>
      )}

      <InstallDialerDialog
        open={dialogOpen}
        connections={activeConnections}
        onClose={() => setDialogOpen(false)}
        onInstalled={onInstalled}
      />

      <ConfirmDialog
        open={!!removal}
        title={removal?.status === "installed" ? "Remove dialer" : "Dismiss install"}
        description={
          removal?.status === "installed" ? (
            <>
              Remove the dialer on{" "}
              <span className="font-medium text-zinc-900 dark:text-zinc-100">
                {removal?.phoneNumber ?? "this number"}
              </span>
              ? The TwiML App will be deleted and the number unbound from it.
            </>
          ) : (
            <>Dismiss this install attempt? Nothing was deployed to Twilio.</>
          )
        }
        confirmLabel={removal?.status === "installed" ? "Remove" : "Dismiss"}
        intent="danger"
        busy={removing}
        onConfirm={() => void confirmRemoval()}
        onCancel={() => setRemoval(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * One row per number: when a number has both a live (installed/pending/running)
 * record and superseded failed attempts, keep the live one. A number with only
 * failed attempts keeps its newest failed record (so it stays visible + can be
 * dismissed or retried). Input is newest-first.
 */
function dedupeByNumber(list: AcceleratorInstallationDTO[]): AcceleratorInstallationDTO[] {
  const groups = new Map<string, AcceleratorInstallationDTO[]>();
  for (const inst of list) {
    const key = inst.phoneNumberSid
      ? `${inst.connectionId}:${inst.phoneNumberSid}`
      : `id:${inst.id}`;
    const g = groups.get(key);
    if (g) g.push(inst);
    else groups.set(key, [inst]);
  }
  const out: AcceleratorInstallationDTO[] = [];
  for (const g of groups.values()) {
    const live = g.find((d) => d.status !== "failed");
    out.push(live ?? g[0]!);
  }
  return out;
}

function AcceleratorCard({
  entry,
  installations,
  connNameById,
  onInstall,
  onRemove,
}: {
  entry: AcceleratorCatalogEntry;
  installations: AcceleratorInstallationDTO[];
  connNameById: Map<string, string>;
  onInstall: () => void;
  onRemove: (inst: AcceleratorInstallationDTO) => void;
}) {
  const liveCount = installations.filter(
    (i) => i.status === "installed" || i.status === "pending" || i.status === "running",
  ).length;

  return (
    <div className="flex flex-col rounded-xl border border-zinc-200 bg-white p-5 shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            {entry.name}
          </h2>
          <p className="mt-1 text-[13px] text-zinc-600 dark:text-zinc-400">{entry.summary}</p>
        </div>
        {liveCount > 0 && (
          <span className="flex-none rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300">
            {liveCount} deployed
          </span>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {entry.deploys.map((d) => (
          <span
            key={d}
            className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10.5px] font-medium text-zinc-600 ring-1 ring-inset ring-zinc-200 dark:bg-zinc-800/60 dark:text-zinc-400 dark:ring-zinc-700"
          >
            {d}
          </span>
        ))}
      </div>

      {installations.length > 0 && (
        <ul className="mt-4 divide-y divide-zinc-100 rounded-md border border-zinc-200 dark:divide-zinc-800/60 dark:border-zinc-800">
          {installations.map((inst) => (
            <li key={inst.id} className="flex items-center gap-3 px-3 py-2">
              <span className="min-w-0 flex-1">
                <span className="block truncate font-mono text-[13px] text-zinc-900 dark:text-zinc-100">
                  {inst.phoneNumber ?? "—"}
                </span>
                <span className="block truncate text-[11.5px] text-zinc-500">
                  {connNameById.get(inst.connectionId) ?? "Unknown account"}
                  {inst.status === "failed" && inst.error ? ` · ${inst.error.message}` : ""}
                </span>
              </span>
              <StatusBadge status={inst.status} />
              <button
                type="button"
                onClick={() => onRemove(inst)}
                aria-label={`Remove dialer on ${inst.phoneNumber ?? "this number"}`}
                title="Remove"
                className="flex h-7 w-7 flex-none items-center justify-center rounded-md text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:hover:bg-zinc-800/60 dark:hover:text-zinc-200"
              >
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
                  <path
                    d="M18 6 6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 flex items-center gap-2 pt-1">
        <Button onClick={onInstall}>{installations.length > 0 ? "Add dialers" : "Install"}</Button>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: AcceleratorInstallationStatus }) {
  const map: Record<AcceleratorInstallationStatus, { label: string; cls: string; dot: string }> = {
    installed: {
      label: "Installed",
      cls: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300",
      dot: "bg-emerald-500 dark:bg-emerald-400",
    },
    pending: {
      label: "Pending",
      cls: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
      dot: "bg-amber-500 dark:bg-amber-400",
    },
    running: {
      label: "Installing",
      cls: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
      dot: "bg-amber-500 dark:bg-amber-400 animate-pulse",
    },
    failed: {
      label: "Failed",
      cls: "border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300",
      dot: "bg-red-500 dark:bg-red-400",
    },
  };
  const s = map[status];
  return (
    <span
      className={
        "inline-flex flex-none items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium " +
        s.cls
      }
    >
      <span className={"h-1.5 w-1.5 rounded-full " + s.dot} />
      {s.label}
    </span>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {Array.from({ length: 2 }).map((_, i) => (
        <div
          key={i}
          className="rounded-xl border border-zinc-200 bg-white p-5 shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none"
        >
          <div className="h-4 w-28 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
          <div className="mt-3 h-3 w-full animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
          <div className="mt-2 h-3 w-2/3 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
          <div className="mt-5 h-9 w-24 animate-pulse rounded-md bg-zinc-200 dark:bg-zinc-800/70" />
        </div>
      ))}
    </div>
  );
}
